import { z } from "zod";
import {
  resourceSearchFamilies,
  type ResourceSearchFamily,
  type ResourceSearchKind,
} from "@/db/definitions";
import type { ResourceSearchData } from "@/lib/types/resource";

export const resourceSearchSortOptions = ["relevance", "recent"] as const;

export type ResourceSearchSort = (typeof resourceSearchSortOptions)[number];

export const RESOURCE_SEARCH_PAGE_SIZE = 24;
export const RESOURCE_SEARCH_MAX_PAGE_SIZE = 48;
export const RESOURCE_SEARCH_MAX_QUERY_LENGTH = 120;

const toArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value.split(",");
  }

  return [];
};

export const resourceSearchFilterSchema = z.object({
  query: z.preprocess(
    (value) =>
      typeof value === "string"
        ? value.slice(0, RESOURCE_SEARCH_MAX_QUERY_LENGTH).trim()
        : "",
    z.string().catch(""),
  ),
  families: z.preprocess(
    (value) =>
      toArray(value).filter((entry) =>
        resourceSearchFamilies.includes(entry as ResourceSearchFamily),
      ),
    z.array(z.enum(resourceSearchFamilies)).catch([]),
  ),
  sort: z.enum(resourceSearchSortOptions).catch("relevance"),
  page: z.coerce.number().int().min(1).catch(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(RESOURCE_SEARCH_MAX_PAGE_SIZE)
    .catch(RESOURCE_SEARCH_PAGE_SIZE),
});

export type ResourceSearchFilters = z.infer<typeof resourceSearchFilterSchema>;

export const getResetResourceSearchFilters = (): ResourceSearchFilters => ({
  query: "",
  families: [],
  sort: "relevance",
  page: 1,
  pageSize: RESOURCE_SEARCH_PAGE_SIZE,
});

export const parseResourceSearchFilter = (
  data: unknown,
): ResourceSearchFilters => {
  const parsed = resourceSearchFilterSchema.safeParse(data);

  if (!parsed.success) {
    return getResetResourceSearchFilters();
  }

  return parsed.data;
};

export const getResourceSearchUrlQuery = (filters: ResourceSearchFilters) => {
  const params = new URLSearchParams();

  if (filters.query) {
    params.set("query", filters.query);
  }

  filters.families.forEach((family) => {
    params.append("families", family);
  });

  if (filters.sort !== "relevance") {
    params.set("sort", filters.sort);
  }

  if (filters.page > 1) {
    params.set("page", filters.page.toString());
  }

  if (filters.pageSize !== RESOURCE_SEARCH_PAGE_SIZE) {
    params.set("pageSize", filters.pageSize.toString());
  }

  return { params };
};

export type ResourceSearchResultItem = {
  key: string;
  family: ResourceSearchFamily;
  kind: ResourceSearchKind;
  path: string;
  shortLabel: string;
  cardSubtitle: string | null;
  cardDescription: string;
  linkTitle: string;
  updatedAt: string;
  data: ResourceSearchData;
};

export type ResourceSearchFamilyCounts = Partial<
  Record<ResourceSearchFamily, number>
>;

export type ResourceSearchResponse = {
  items: ResourceSearchResultItem[];
  count: number;
  familyCounts: ResourceSearchFamilyCounts;
};
